import { ApiError, apiFetch } from "@/lib/auth";

export type ConnType = "rdp" | "ssh" | "vnc" | "web" | "desktop";

export const TYPE_LABEL: Record<ConnType, string> = {
  rdp: "RDP",
  ssh: "SSH",
  vnc: "VNC",
  web: "Web",
  desktop: "Escritorio",
};

export type ConnectionOut = {
  id: string;
  tenant_id: string;
  folder_id: string | null;
  name: string;
  type: ConnType;
  host: string;
  port: number | null;
  params: Record<string, unknown> | null;
  credential_id: string | null;
  agent_id: string | null;
  /** Desktop console provenance: the RDP connection it was created from. */
  desktop_of_connection_id?: string | null;
  preferred_connector_id?: string | null;
  /** Live desktop sessions on this host (detail of desktop connections only). */
  desktop_sessions_active?: number | null;
  /** Requestor's effective role on this connection (fix-invitations-and-role-ui). */
  effective_role?: string | null;
};

export type ConnectionInput = {
  type: ConnType;
  name: string;
  host?: string;
  port?: number | null;
  folder_id: string | null;
  credential_id?: string | null;
  agent_id?: string | null;
  desktop_of_connection_id?: string | null;
} & Partial<Record<"width" | "height" | "quality" | "color_depth", number>> &
  Partial<{
    url: string;
    headers: Record<string, string>;
    kex: string[];
    ciphers: string[];
    macs: string[];
    terminal: string;
  }>;

const PARAM_KEYS: Record<ConnType, string[]> = {
  rdp: ["width", "height", "color_depth"],
  ssh: ["kex", "ciphers", "macs", "terminal"],
  vnc: ["quality", "color_depth"],
  web: ["url", "headers"],
  desktop: [],
};

export function splitPayload(
  input: Record<string, unknown>,
): { base: Record<string, unknown>; params: Record<string, unknown> } {
  const type = input.type as ConnType;
  const keys = new Set(PARAM_KEYS[type]);
  const base: Record<string, unknown> = {};
  const params: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (keys.has(k)) params[k] = v;
    else if (k !== "type" && v !== null && v !== "") base[k] = v;
  }
  return { base, params };
}

export function listConnections(
  filters: { q?: string; type?: string; folder_id?: string } = {},
): Promise<{ connections: ConnectionOut[] }> {
  const search = new URLSearchParams();
  if (filters.q) search.set("q", filters.q);
  if (filters.type) search.set("type", filters.type);
  if (filters.folder_id) search.set("folder_id", filters.folder_id);
  const qs = search.toString();
  return apiFetch(`/api/connections${qs ? `?${qs}` : ""}`);
}

export function getConnection(id: string): Promise<ConnectionOut> {
  return apiFetch(`/api/connections/${id}`);
}

export async function createConnection(input: ConnectionInput): Promise<ConnectionOut> {
  const { base, params } = splitPayload(input);
  return apiFetch("/api/connections", {
    method: "POST",
    body: JSON.stringify({ ...base, type: input.type, ...params }),
  });
}

export async function updateConnection(
  id: string,
  input: ConnectionInput,
): Promise<ConnectionOut> {
  const { base, params } = splitPayload(input);
  const body: Record<string, unknown> = { ...base, ...params };
  delete body.type; // type is immutable
  if (body.folder_id === null) delete body.folder_id;
  return apiFetch(`/api/connections/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function deleteConnection(id: string): Promise<void> {
  await apiFetch<undefined>(`/api/connections/${id}`, { method: "DELETE" });
}

/** Link a connection to its host agent (redesign-app-panels). */
export async function linkConnectionAgent(
  id: string,
  agentId: string,
): Promise<ConnectionOut> {
  return apiFetch(`/api/connections/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ agent_id: agentId }),
  });
}

/** Unlink the host agent (add-terminal-prefs-unlink-agent): explicit null. */
export async function unlinkConnectionAgent(id: string): Promise<ConnectionOut> {
  return apiFetch(`/api/connections/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ agent_id: null }),
  });
}

/** Set/clear the per-connection connector preference
 * (add-reactive-lists-connector-pref): empty string clears it (explicit null). */
export async function setPreferredConnector(
  id: string,
  connectorId: string | null,
): Promise<ConnectionOut> {
  return apiFetch(`/api/connections/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ preferred_connector_id: connectorId }),
  });
}

export type TestResult = { ok: boolean; latency_ms?: number; error?: string };

export function testConnection(id: string, connectorId?: string): Promise<TestResult> {
  const q = connectorId ? `?connector_id=${encodeURIComponent(connectorId)}` : "";
  return apiFetch(`/api/connections/${id}/test${q}`, { method: "POST" });
}

export async function emptyFolder(id: string): Promise<{ deleted: number }> {
  return apiFetch(`/api/folders/${id}/connections`, { method: "DELETE" });
}

export { ApiError };
